import React, { useState, useEffect, useRef } from 'react';
import { Container, Form, Button, Card, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import JoditEditor from 'jodit-react';
import { addPage, getPageById, updatePage } from '../../services/pageService';

const PageForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  
  const [pageData, setPageData] = useState({
    title: '',
    url: '',
    content: '',
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validated, setValidated] = useState(false);
  const editor = useRef(null);

  // Jodit editor configuration
  const editorConfig = {
    readonly: false,
    height: 500,
    toolbar: true,
    spellcheck: false,
    language: 'tr',
    toolbarButtonSize: 'middle',
    toolbarAdaptive: false,
    uploader: {
      insertImageAsBase64URI: true
    },
    buttons: [
      'source', '|',
      'bold', 'strikethrough', 'underline', 'italic', '|',
      'ul', 'ol', '|',
      'outdent', 'indent', '|',
      'font', 'fontsize', 'brush', 'paragraph', '|',
      'image', 'video', 'table', 'link', '|',
      'align', 'undo', 'redo', '|',
      'hr', 'eraser', 'fullsize'
    ]
  };

  useEffect(() => {
    if (isEditMode) {
      fetchPageDetails();
    }
  }, [id]);

  const fetchPageDetails = async () => {
    try {
      setLoading(true);
      const data = await getPageById(id);
      if (data) {
        setPageData({
          guid: data.guid,
          title: data.title || '',
          url: data.url || '',
          content: data.content || '',
          isActive: data.isActive !== undefined ? data.isActive : true
        });
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching page details:', err);
      setError('Sayfa detayları yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPageData({
      ...pageData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleContentChange = (newContent) => {
    setPageData({
      ...pageData,
      content: newContent
    });
  };

  const handleUrlGeneration = () => {
    if (pageData.title) {
      // Create a URL-friendly string from the title
      const url = pageData.title
        .toLowerCase()
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ı/g, 'i')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c')
        .replace(/[^a-z0-9\s-]/g, '')  // Remove special chars
        .replace(/\s+/g, '-')          // Replace spaces with hyphens
        .replace(/-+/g, '-')           // Remove consecutive hyphens
        .trim();
        
      setPageData({
        ...pageData,
        url
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    try {
      setLoading(true);
      if (isEditMode) {
        await updatePage(pageData);
      } else {
        await addPage(pageData);
      }
      navigate('/pages');
    } catch (err) {
      console.error('Error saving page:', err);
      setError(`Sayfa ${isEditMode ? 'güncellenirken' : 'eklenirken'} bir hata oluştu.`);
      setLoading(false);
    }
  };

  if (loading && isEditMode && !pageData.title) {
    return (
      <Container fluid className="main-content-container px-4 py-4">
        <div className="text-center my-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Yükleniyor...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="main-content-container px-4 py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="m-0">{isEditMode ? 'Sayfayı Düzenle' : 'Yeni Sayfa Ekle'}</h1>
        <Button variant="outline-secondary" onClick={() => navigate('/pages')}>
          Geri Dön
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body>
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Sayfa Başlığı*</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={pageData.title}
                    onChange={handleChange}
                    onBlur={handleUrlGeneration}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Lütfen bir sayfa başlığı girin.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Sayfa URL'i*</Form.Label>
                  <Form.Control
                    type="text"
                    name="url"
                    value={pageData.url}
                    onChange={handleChange}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Lütfen bir URL girin.
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Başlık girdiğinizde otomatik oluşturulur, düzenleyebilirsiniz.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="isActive"
                label="Aktif"
                checked={pageData.isActive}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Sayfa İçeriği*</Form.Label>
              <JoditEditor
                ref={editor}
                value={pageData.content}
                config={editorConfig}
                onBlur={handleContentChange}
                onChange={handleContentChange}
              />
              {validated && !pageData.content && (
                <div className="text-danger mt-2">
                  Lütfen sayfa içeriğini girin.
                </div>
              )}
            </Form.Group>

            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="primary"
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner 
                      as="span" 
                      animation="border" 
                      size="sm" 
                      role="status" 
                      aria-hidden="true" 
                      className="me-2"
                    />
                    İşleniyor...
                  </>
                ) : (
                  isEditMode ? 'Güncelle' : 'Yayınla'
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PageForm;
